// See NumberInput.tsx for more explanation
import { useEffect, useRef } from "react";
import { IntlShape, useIntl } from "react-intl";

import inputElementStyles from "../styles/InputElements.module.css";

import styles from "./DurationInput.module.css";

interface Props {
  value: string; // hh:mm
  setValue: (value: string) => void;
  disabled?: boolean;
}

const isHourValid = (value: string, intl: IntlShape) => {
  if (value[0] === "0" && value.length > 1) {
    return {
      valid: false,
      message: intl.formatMessage({
        id: "duration_input.not_start_zero",
        defaultMessage: "Should not start with zero",
      }),
    };
  }
  if (value === "") {
    return {
      valid: false,
      message: intl.formatMessage({
        id: "duration_input.value_cannot_be_empty",
        defaultMessage: "Value cannot be empty",
      }),
    };
  }
  return {
    valid: true,
    message: intl.formatMessage({
      id: "duration_input.no_problems_with_input",
      defaultMessage: "No problems found",
    }),
  };
};

const isMinuteValid = (value: string, intl: IntlShape) => {
  if (value.length !== 2) {
    return {
      valid: false,
      message: intl.formatMessage({
        id: "duration_input.minute_must_be_2_long",
        defaultMessage: "Must have 2 digits. For example '08'",
      }),
    };
  }
  if (parseFloat(value) > 59) {
    return {
      valid: false,
      message: intl.formatMessage({
        id: "duration_input.minute_too_high",
        defaultMessage: "Maximal 59 minutes",
      }),
    };
  }

  return {
    valid: true,
    message: intl.formatMessage({
      id: "duration_input.no_problems_with_input",
      defaultMessage: "No problems found",
    }),
  };
};

function NumberInput(props: Props) {
  const { value, setValue, disabled } = props;

  const myHourInput = useRef<HTMLInputElement>(null);
  const myMinuteInput = useRef<HTMLInputElement>(null);
  const intl = useIntl();
  const hourValue = value.split(":")[0];
  const minuteValue = value.split(":")[1] || "";
  const minuteValid = isMinuteValid(minuteValue, intl);
  const hourValid = isHourValid(hourValue, intl);

  useEffect(() => {
    if (myMinuteInput && myMinuteInput.current) {
      // valueValid is current validity based upon this component itself, invalidMessage is based on if this component received a invalid message as prop
      if (minuteValid.valid) {
        myMinuteInput.current.setCustomValidity("");
      } else {
        myMinuteInput.current.setCustomValidity(minuteValid.message);
      }
    }

    if (hourValue === "0" && minuteValue === "00") {
      hourValid.message = intl.formatMessage({
        id: "duration_input.total_duration_cannot_zero",
        defaultMessage: "Total duration must be greater than 0:00",
      });
      hourValid.valid = false;
    }

    if (myHourInput && myHourInput.current) {
      // valueValid is current validity based upon this component itself, invalidMessage is based on if this component received a invalid message as prop
      if (hourValid.valid) {
        myHourInput.current.setCustomValidity("");
      } else {
        myHourInput.current.setCustomValidity(hourValid.message);
      }
    }
  }, [value, hourValid, hourValue, intl, minuteValid, minuteValue]);

  return (
    <span className={styles.DurationInput}>
      <input
        ref={myHourInput}
        className={`${inputElementStyles.Padding} ${inputElementStyles.Input} ${
          hourValid.valid ? "" : inputElementStyles.Invalid
        } ${disabled ? inputElementStyles.Disabled : ""}`}
        // type="number" bugs when we paste text
        type="text"
        required
        value={hourValue}
        disabled={disabled}
        onKeyPress={(event) => {
          // only allow legel keypresses to pass
          // this makes sure the onChange is not even called with other keys and thus also the cursor does not change on those keys
          // also backspace delete arrows and tab are still handled
          const possibleChars = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];
          if (!possibleChars.includes(event.key)) {
            if (event.preventDefault) event.preventDefault();
            return false;
          }
        }}
        onChange={(e) => {
          const eventValue = e.target.value;
          // needed for if text is pasted instead of keyed in
          const replaced = eventValue.replace(/[^0-9]/g, "");
          setValue(replaced + ":" + minuteValue);
        }}
      />
      <span className={styles.HourMinuteSeperator}>
        <span>:</span>
      </span>
      <input
        ref={myMinuteInput}
        className={`${inputElementStyles.Padding} ${inputElementStyles.Input} ${
          minuteValid.valid ? "" : inputElementStyles.Invalid
        } ${disabled ? inputElementStyles.Disabled : ""}`}
        // type="number" commented out deliberately willnot work together with pattern
        // type="number" bugs when we paste text
        type="text"
        required
        value={minuteValue}
        disabled={disabled}
        onKeyPress={(event) => {
          // only allow legel keypresses to pass
          // this makes sure the onChange is not even called with other keys and thus also the cursor does not change on those keys
          // also backspace delete arrows and tab are still handled
          const possibleChars = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];
          if (!possibleChars.includes(event.key)) {
            if (event.preventDefault) event.preventDefault();
            return false;
          }
        }}
        onChange={(e) => {
          const eventValue = e.target.value;
          // needed for if text is pasted instead of keyed in
          const replaced = eventValue.replace(/[^0-9]/g, "");
          setValue(hourValue + ":" + replaced);
        }}
      />
      <span className={`${styles.UnitHour} ${inputElementStyles.Padding}`}>h</span>
      <span className={`${styles.UnitMinute} ${inputElementStyles.Padding}`}>min</span>
    </span>
  );
}

export default NumberInput;
